<template>
  <div
    class="absolute top-0 left-0 w-full h-full flex items-center justify-center z-[1]"
  >
    <div class="relative">
      <VStage
        ref="konvaStage"
        class="absolute top-0 left-0"
        :config="stageConfig"
        @mousedown="interactions.handleStageClick"
        @tap="interactions.handleStageClick"
      >
        <VLayer ref="konvaLayer">
          <VRect
            v-for="quadrant in state.simpleQuadrants.value"
            :key="quadrant.id || quadrant.name"
            :config="getQuadrantConfig(quadrant)"
          />
          <template
            v-if="state.customerLogo.value && state.logoImageLoaded.value"
          >
            <VImage
              v-for="config in state.logoConfigs.value"
              :key="`logo-${config.quadrantId}`"
              ref="logoImages"
              :config="{
                ...config,
                name: 'logo',
                id: `logo-quadrant-${config.quadrantId}`,
                draggable: true,
                listening: true,
                dragBoundFunc: interactions.createDragBoundFunc(
                  config.quadrantId,
                ),
              }"
              @mousedown="(e) => handleLogoClick(e.target)"
              @dragstart="(e) => handleLogoClick(e.target)"
              @click="(e) => handleLogoClick(e.target)"
              @dblclick="(e) => handleLogoDoubleClick(e.target, config)"
              @dragend="(e) => handleLogoDragEnd(e.target)"
              @transformend="(e) => handleTransformEnd(e.target)"
            />
          </template>
          <VTransformer ref="konvaTransformer" :config="transformerConfig" />
        </VLayer>
      </VStage>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';
import { useQuadrants } from '~/composables/use-quadrants';

type OverlayProps = {
  stageConfig: any;
  transformerConfig: any;
  state: {
    simpleQuadrants: { value: any[] };
    customerLogo: { value: any };
    logoImageLoaded: { value: boolean };
    logoConfigs: { value: any[] };
    isLogoSelected: { value: boolean };
    selectedLogoQuadrant: { value: any };
  };
  interactions: {
    handleLogoClick: (target: any) => void;
    handleLogoRemove: (quadrantId: string | number) => void;
    createDragBoundFunc: (
      quadrantId: string | number,
    ) => (pos: { x: number; y: number }) => { x: number; y: number };
    isQuadrantOccupied: (quadrant: any) => boolean;
    establishLogoHomeQuadrant: (target: any) => void;
  };
};

const props = defineProps<OverlayProps>();

const emit = defineEmits<{
  'logo-click': [target: any];
  'logo-remove': [quadrantId: string | number];
  'logo-transform-end': [target: any];
  'logo-drag-end': [target: any];
}>();

const konvaStage = ref<any>(null);
const konvaLayer = ref<any>(null);
const konvaTransformer = ref<any>(null);
const konvaImageLayer = ref<any>(null);
const logoImages = ref<any[]>([]);

const { VStage, VLayer, VRect, VImage, VTransformer } = useQuadrants({
  emit: (..._args: any[]) => null,
  stage: konvaStage,
  layer: konvaLayer,
  transformer: konvaTransformer,
  imageLayer: konvaImageLayer,
  quadrants: [],
});

const handleLogoClick = (target: any) => {
  props.interactions.handleLogoClick(target);
  emit('logo-click', target);
};

const handleLogoDoubleClick = (target: any, config: any) => {
  const logoId = target.id();
  const quadrantId = logoId.replace('logo-quadrant-', '');
  props.interactions.handleLogoRemove(quadrantId);
  emit('logo-remove', quadrantId);
};

const handleLogoDragEnd = (target: any) => {
  emit('logo-drag-end', target);
};

const handleTransformEnd = (target: any) => {
  emit('logo-transform-end', target);
};

watch(
  () => props.state.logoConfigs.value,
  () => {
    nextTick(() => {
      for (const logoRef of logoImages.value) {
        if (logoRef?.getNode) {
          const logoNode = logoRef.getNode();
          props.interactions.establishLogoHomeQuadrant(logoNode);
        }
      }
    });
  },
  { immediate: true },
);

const getQuadrantConfig = (quadrant: any) => {
  const isSelected =
    props.state.isLogoSelected.value &&
    props.state.selectedLogoQuadrant.value?.id === quadrant.id;
  const isOccupied = props.interactions.isQuadrantOccupied(quadrant);

  return {
    ...quadrant,
    fill: 'rgba(4, 41, 87, 1)',
    opacity: 0.5,
    strokeScaleEnabled: false,
    draggable: false,
    listening: true,
  };
};

defineExpose({
  konvaStage,
  konvaTransformer,
});
</script>
